import './rater.footer.scss';

import { Box, Typography } from '@mui/material';
import {
  CurrencyDisplay,
  NumberField,
  RaterNotesButton,
  RaterNotesRow,
} from '../common';
import { NoteTargetName, NoteTargetType } from '@converge/graphql';
import { limitNumberToTwoDigits, roundNumber } from '@converge/utils';
import {
  underwritingQuoteToUpsertVariables,
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { RaterQuoteSaveButton } from './rater.quote.save.button';
import { RaterTab } from '../types';
import { Stack } from '@mui/system';
import { Sync } from '@mui/icons-material';
import { getParsedValue } from '../coverage-rating';
import { useMemo } from 'react';

const DEBUG = false;

export const RaterFooter = () => {
  const {
    excessConvergeQuotedPremium,
    excessConvergeLimit,
    excessConvergePartOfLimit,
    excessConvergeAttachmentPoint,
    excessConvergeQuotaShare,
    excessConvergeUnderlyingPremiumPercent,
    excessConvergeIncreasedLimitFactor,
    excessConvergeQuotedToBenchmarkRatio,
    ratedPremium,
    technicalPremium,
    limit,
    retention,
    isRequestingRatingUpdate,
    tab,
    isEditable,
    debugVariables,
    uuid,
    excessConvergeLimitHasChanged,
    excessConvergeQuotedPremiumHasChanged,
    excessConvergePartOfLimitHasChanged,
    excessConvergeAttachmentPointHasChanged,
    excessConvergeQuotaShareHasChanged,
    excessConvergeUnderlyingPremiumPercentHasChanged,
    excessConvergeIncreasedLimitFactorHasChanged,
    excessConvergeQuotedToBenchmarkRatioHasChanged,
    ratedPremiumHasChanged,
    basePremiumHasChanged,
    coverages,
    technicalPremiumHasChanged,
    limitHasChanged,
    retentionHasChanged,
    quotedRatedRatio,
    selectedQuotedPremium,
  } = useRaterStateContextSelector(
    ({
      quote,
      isRequestingRatingUpdate,
      mode,
      tab,
      userQuotes,
      isEditable,
      submission,
      quote: {
        uuid,
        excessConvergeQuotedPremium,
        excessConvergeLimit,
        excessConvergePartOfLimit,
        excessConvergeAttachmentPoint,
        excessConvergeQuotaShare,
        excessConvergeUnderlyingPremiumPercent,
        excessConvergeIncreasedLimitFactor,
        excessConvergeQuotedToBenchmarkRatio,
        ratedPremium,
        selectedQuotedPremium,
        technicalPremium,
        limit,
        retention,
        coverages,
        quotedRatedRatio,
      },
      changedFields,
    }) => {
      return {
        excessConvergeLimit,
        excessConvergeQuotedPremium,
        excessConvergePartOfLimit,
        excessConvergeAttachmentPoint,
        excessConvergeQuotaShare,
        excessConvergeUnderlyingPremiumPercent,
        excessConvergeIncreasedLimitFactor,
        excessConvergeQuotedToBenchmarkRatio,
        ratedPremium,
        technicalPremium,
        limit,
        isEditable,
        coverages,
        retention,
        quotedRatedRatio,
        selectedQuotedPremium,
        excessConvergeQuotedPremiumHasChanged: changedFields.includes(
          'excessConvergeQuotedPremium'
        ),
        excessConvergeLimitHasChanged: changedFields.includes(
          'excessConvergeLimit'
        ),
        excessConvergePartOfLimitHasChanged: changedFields.includes(
          'excessConvergePartOfLimit'
        ),
        excessConvergeAttachmentPointHasChanged: changedFields.includes(
          'excessConvergeAttachmentPoint'
        ),
        excessConvergeQuotaShareHasChanged: changedFields.includes(
          'excessConvergeQuotaShare'
        ),
        excessConvergeUnderlyingPremiumPercentHasChanged:
          changedFields.includes('excessConvergeUnderlyingPremiumPercent'),
        excessConvergeIncreasedLimitFactorHasChanged: changedFields.includes(
          'excessConvergeIncreasedLimitFactor'
        ),
        excessConvergeQuotedToBenchmarkRatioHasChanged: changedFields.includes(
          'excessConvergeQuotedToBenchmarkRatio'
        ),
        ratedPremiumHasChanged: changedFields.includes('ratedPremium'),
        basePremiumHasChanged: changedFields.includes('basePremium'),
        technicalPremiumHasChanged: changedFields.includes('technicalPremium'),
        limitHasChanged: changedFields.includes('limit'),
        retentionHasChanged: changedFields.includes('retention'),
        uuid,
        isRequestingRatingUpdate,
        mode,
        tab,
        userQuotes,
        submission,
        debugVariables: DEBUG
          ? JSON.stringify(
              underwritingQuoteToUpsertVariables({
                mode,
                quote,
                submissionUuid: submission.uuid,
                totalUserQuotes: userQuotes.pageInfo.totalResults,
              }),
              null,
              2
            )
          : undefined,
      };
    }
  );

  const { dispatch, actions } = useRaterActionDispatchContext();
  const handleUpdatePremium = (newValue: number | null) => {
    const parsedValue = getParsedValue(newValue);

    dispatch(actions.updatePremium(parsedValue ?? 0));
  };

  const opacity = isRequestingRatingUpdate ? 0.75 : undefined;

  const isOnExcessTab = tab === RaterTab.ExcessRating;
  const roundQuotedRatedRatio =
    quotedRatedRatio && quotedRatedRatio !== null && quotedRatedRatio !== 1
      ? `${(quotedRatedRatio * 100).toFixed(3)} % `
      : quotedRatedRatio && quotedRatedRatio === 1
      ? `${quotedRatedRatio * 100} %`
      : '--';

  const totalBasePremium = useMemo(() => {
    return coverages?.reduce(
      (acc, coverage) => acc + (coverage?.quote?.basePremium || 0),
      0
    );
  }, [coverages]);

  const summary = isOnExcessTab ? (
    <>
      <Col
        label="Premium"
        type="currency"
        hasChanged={excessConvergeQuotedPremiumHasChanged}
      >
        {excessConvergeQuotedPremium}
      </Col>
      <Col
        label="Limit"
        type="currency"
        hasChanged={excessConvergeLimitHasChanged}
      >
        {excessConvergeLimit}
      </Col>
      <Col
        label="Excess layer limit"
        type="currency"
        hasChanged={excessConvergePartOfLimitHasChanged}
      >
        {excessConvergePartOfLimit}
      </Col>
      <Col
        label="Attachment point"
        type="currency"
        hasChanged={excessConvergeAttachmentPointHasChanged}
      >
        {excessConvergeAttachmentPoint}
      </Col>
      <Col
        label="Quota share"
        type="percent"
        hasChanged={excessConvergeQuotaShareHasChanged}
      >
        {excessConvergeQuotaShare}
      </Col>
      <Col
        label="% of underlying premium"
        type="percent"
        hasChanged={excessConvergeUnderlyingPremiumPercentHasChanged}
      >
        {excessConvergeUnderlyingPremiumPercent}
      </Col>
      <Col
        label="ILF"
        type="percent"
        hasChanged={excessConvergeIncreasedLimitFactorHasChanged}
      >
        {excessConvergeIncreasedLimitFactor}
      </Col>
      <Col
        label="Premium to Benchmark Premium Ratio"
        type="percent"
        hasChanged={excessConvergeQuotedToBenchmarkRatioHasChanged}
      >
        {excessConvergeQuotedToBenchmarkRatio}
      </Col>
    </>
  ) : (
    <>
      <Stack marginX={2} sx={{ opacity }}>
        <Box className="premium-container">
          <NumberField
            name="PREMIUM"
            hasLabelBold={true}
            placeholder={`${roundNumber(ratedPremium)?.toLocaleString()}`}
            value={
              roundNumber(selectedQuotedPremium)
                ? limitNumberToTwoDigits(roundNumber(selectedQuotedPremium))
                : ''
            }
            isDisabled={!isEditable}
            onBlur={handleUpdatePremium}
            onChange={(value) => {
              if (!value && value !== 0) {
                handleUpdatePremium(null);
              }
            }}
            asValue
            isCurrency
          />
        </Box>
      </Stack>
      <Stack marginX={2} sx={{ opacity }} className="footer-items">
        <CurrencyDisplay
          label={'Rated Premium'}
          size={'normal'}
          hasChanged={ratedPremiumHasChanged}
        >
          {roundNumber(ratedPremium)}
        </CurrencyDisplay>
      </Stack>
      <Stack marginX={2} sx={{ opacity }} className="footer-items">
        <CurrencyDisplay
          label={'Quoted Ratio'}
          size={'normal'}
          hasChanged={ratedPremiumHasChanged}
          notCurrency={false}
        >
          {`${roundQuotedRatedRatio} `}
        </CurrencyDisplay>
      </Stack>
      <Stack marginX={2} sx={{ opacity }} className="footer-items">
        <CurrencyDisplay
          label="Total Technical Premium"
          hasChanged={technicalPremiumHasChanged}
        >
          {roundNumber(technicalPremium)}
        </CurrencyDisplay>
      </Stack>
      <Stack marginX={2} sx={{ opacity }} className="footer-items">
        <CurrencyDisplay
          label="Total Base Premium"
          hasChanged={basePremiumHasChanged}
        >
          {roundNumber(totalBasePremium)}
        </CurrencyDisplay>
      </Stack>
      <Stack marginX={2} sx={{ opacity }} className="footer-items">
        <CurrencyDisplay label="Limit" hasChanged={limitHasChanged}>
          {roundNumber(limit)}
        </CurrencyDisplay>
      </Stack>
      <Stack marginX={2} sx={{ opacity }} className="footer-items">
        <CurrencyDisplay label="Retention" hasChanged={retentionHasChanged}>
          {roundNumber(retention)}
        </CurrencyDisplay>
      </Stack>
    </>
  );

  return (
    <Stack className="rater-footer-main-container">
      <Stack className="summery-container ">
        <Stack className="summery-table ">
          <Stack className="note-footer">
            <RaterNotesButton
              targetName={NoteTargetName.Quote}
              targetType={NoteTargetType.Node}
              uuid={uuid}
            />
          </Stack>
          {summary}
        </Stack>
        <Stack
          className="close-button"
          style={{ marginTop: !isOnExcessTab ? '17px' : '0px' }}
        >
          <RaterQuoteSaveButton />
        </Stack>
      </Stack>
      <Stack>
        <RaterNotesRow
          targetName={NoteTargetName.Quote}
          targetType={NoteTargetType.Node}
          uuid={uuid}
          isFullWidth={true}
          colSpan={0}
        />
      </Stack>
      {DEBUG && (
        <textarea className="debug-textarea" disabled readOnly>
          {debugVariables}
        </textarea>
      )}
    </Stack>
  );
};

interface IColProps {
  children: React.ReactNode;
  label: string;
  opacity?: number;
  type?: 'currency' | 'percent';
  round?: boolean;
  size?: 'large' | 'normal';
  loading?: boolean;
  hasChanged?: boolean;
}

const Col = ({
  children,
  label,
  opacity,
  type = 'currency',
  round = true,
  size,
  loading,
  hasChanged,
}: IColProps) => {
  const labelComp = (
    <>
      {label}
      {hasChanged && '*'} {loading && <Sync className="spin" />}
    </>
  );
  return (
    <Stack marginX={2} sx={{ opacity }}>
      {type === 'currency' ? (
        <CurrencyDisplay label={labelComp} size={size}>
          {round ? roundNumber(children as number) : (children as number)}
        </CurrencyDisplay>
      ) : type === 'percent' ? (
        <>
          <Typography fontSize={'small'}>{labelComp}</Typography>

          <Typography fontWeight="bold" color="primary">
            {((children as number) * 100).toFixed(0)}%
          </Typography>
        </>
      ) : (
        <>
          <Typography fontSize={'small'}>{labelComp}</Typography>

          {children}
        </>
      )}
    </Stack>
  );
};
